import type {
  ApiError as ApiErrorPayload,
  BillingSummary,
  Subscription,
  SubscriptionPlan,
} from '@worki/shared';
import * as platform from '@platform';
import { apiUrl, clientId, isNative } from './config.js';

/**
 * Thin fetch wrapper.
 *
 * The API answers with i18n keys, never sentences, so this carries the key and
 * the per-field key map through to the UI unchanged; translation happens at the
 * render site.
 */
export class RequestError extends Error {
  readonly status: number;
  /** i18n key, e.g. `errors.emailTaken`. */
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, fields: Record<string, string> = {}) {
    super(code);
    this.name = 'RequestError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/**
 * Notified when any request comes back 401, so a session that stopped being
 * valid mid-visit drops the user back to login instead of leaving a signed-in
 * screen mounted and retrying forever. Registered by `SessionProvider`.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/** In-memory copy of the native session token, so keepalive fetches stay sync. */
let memoryToken: string | null | undefined;

async function nativeToken(): Promise<string | null> {
  if (!isNative()) return null;
  if (memoryToken !== undefined) return memoryToken;
  memoryToken = await platform.secureGet('session');
  return memoryToken;
}

async function persistNativeToken(token: string): Promise<void> {
  memoryToken = token;
  await platform.secureSet('session', token);
}

async function clearNativeToken(): Promise<void> {
  memoryToken = null;
  if (isNative()) await platform.secureDelete('session');
}

function localeTag(): string {
  try {
    const stored = localStorage.getItem('worki.locale');
    return stored === 'en' || stored === 'he' ? stored : 'he';
  } catch {
    return 'he';
  }
}

function isAuthPath(path: string): boolean {
  return path.includes('/auth/');
}

export async function apiHeaders(extra?: HeadersInit, anonymous = false): Promise<Headers> {
  const headers = new Headers(extra);
  headers.set('X-Worki-Client', clientId());
  headers.set('Accept-Language', localeTag());
  if (!anonymous) {
    const token = await nativeToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function buildQuery(params: Query): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Send a FormData payload instead of JSON (logo upload). */
  form?: FormData;
  signal?: AbortSignal;
  keepalive?: boolean;
  /**
   * Omit Authorization. Native Google sign-in must not send a leftover Bearer —
   * the ID token is the proof, and a stale session JWT would ride along unused
   * or confuse a 401 from Google with "this app session died".
   */
  anonymous?: boolean;
}

function tokenFrom(payload: unknown): string | null {
  if (payload && typeof payload === 'object' && 'token' in payload) {
    const token = (payload as { token: unknown }).token;
    if (typeof token === 'string' && token) return token;
  }
  return null;
}

/** 202 Google handoff / awaiting-approval — not a session, never a Bearer. */
function isPendingPayload(payload: unknown): boolean {
  return Boolean(
    payload && typeof payload === 'object' && 'pending' in payload && (payload as { pending: unknown }).pending === true,
  );
}

async function onUnauthorizedIfNeeded(path: string, status: number): Promise<void> {
  if (status !== 401 || isAuthPath(path)) return;
  // Stale Keychain tokens re-send forever unless they are dropped first.
  await clearNativeToken();
  onUnauthorized?.();
}

export async function authedFetch(
  path: string,
  init: RequestInit = {},
  anonymous = false,
): Promise<Response> {
  const url = /^https?:\/\//i.test(path) || path.startsWith('blob:') ? path : apiUrl(path);
  const headers = await apiHeaders(init.headers, anonymous);
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'include', ...init, headers });
  } catch {
    throw new RequestError(0, 'errors.network');
  }
  await onUnauthorizedIfNeeded(path, response.status);
  return response;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (!options.form && options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await authedFetch(
      path,
      {
        method: options.method ?? 'GET',
        ...(options.form
          ? { body: options.form }
          : options.body !== undefined
            ? { body: JSON.stringify(options.body) }
            : {}),
        headers,
        ...(options.signal ? { signal: options.signal } : {}),
        ...(options.keepalive ? { keepalive: true } : {}),
      },
      options.anonymous,
    );
  } catch (error) {
    if (error instanceof RequestError) throw error;
    throw new RequestError(0, 'errors.network');
  }

  if (response.status === 204) {
    if (isNative() && path === '/auth/logout') await clearNativeToken();
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : null;

  // 2xx including 202. Native Google returns 202 for a new-user handoff;
  // /google/complete returns 202 when the account is awaiting approval.
  // Neither is a failure — `response.ok` is already true for 202, and this
  // comparison makes that contract visible.
  if (response.status >= 400) {
    const error = (payload as ApiErrorPayload | null)?.error;
    throw new RequestError(
      response.status,
      error?.code || error?.message || 'errors.unknown',
      error?.fields ?? {},
    );
  }

  if (isNative()) {
    if (path === '/auth/logout') await clearNativeToken();
    else if (!isPendingPayload(payload)) {
      const token = tokenFrom(payload);
      if (token) await persistNativeToken(token);
    }
  }

  return payload as T;
}

/**
 * A file, saved onto the device.
 *
 * Not `window.open` and not a `target="_blank"` link, both of which ask a
 * second browsing context to fetch the file for us. That is the version this
 * replaced, and it fails in ways nobody can see from here: a popup blocker
 * returns `null` and the click does nothing at all, an in-app browser (a link
 * opened from WhatsApp or Gmail) either refuses to open a tab or hands the
 * download to the system's own downloader — which has none of our cookies, so
 * an authenticated file comes back 401 as "download failed".
 *
 * Fetched here instead: credentials ride this request the same as on every
 * other, the bytes become a blob this page already holds, and saving it is
 * `platform.saveBytes` rather than a new navigation. A failure is a thrown
 * `RequestError` the caller can put on the screen, which is the other half of
 * the old problem — silence.
 */
async function download(path: string, fallbackName: string): Promise<void> {
  const { blob, name } = await fetchFile(path, fallbackName);
  await platform.saveBytes(blob, name);
}

/**
 * Open an authenticated file: a blob URL in the browser, the share sheet on native.
 */
async function openFile(path: string, fallbackName: string): Promise<void> {
  const { blob, name } = await fetchFile(path, fallbackName);
  if (isNative()) {
    await platform.saveBytes(blob, name);
    return;
  }
  const objectUrl = URL.createObjectURL(blob);
  await platform.openExternal(objectUrl);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

async function fetchFile(path: string, fallbackName: string): Promise<{ blob: Blob; name: string }> {
  let response: Response;
  try {
    response = await authedFetch(path);
  } catch (error) {
    if (error instanceof RequestError) throw error;
    throw new RequestError(0, 'errors.network');
  }

  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? ((await response.json()) as ApiErrorPayload | null) : null;
    throw new RequestError(
      response.status,
      payload?.error?.code ?? 'errors.unknown',
      payload?.error?.fields ?? {},
    );
  }

  return {
    blob: await response.blob(),
    name: fileNameFrom(response.headers.get('Content-Disposition')) ?? fallbackName,
  };
}

/** The name the server sent (RFC 6266): the UTF-8 form first, the ASCII one after. */
function fileNameFrom(header: string | null): string | null {
  if (!header) return null;
  const extended = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (extended) {
    try {
      return decodeURIComponent(extended);
    } catch {
      // A header we cannot decode is not a reason to refuse the file.
    }
  }
  return header.match(/filename="([^"]*)"/i)?.[1] || null;
}

/**
 * A POST whose answer is a file rather than JSON — the quick scan's merge.
 *
 * `request` reads the body as JSON when the server says so and drops it
 * otherwise, which is right for every other route and wrong for this one: here
 * the bytes are the answer. The error path is the same as everywhere else, so a
 * refused scan still arrives as a `RequestError` with the field on it.
 */
async function uploadBlob(path: string, form: FormData): Promise<{ blob: Blob; headers: Headers }> {
  let response: Response;
  try {
    response = await authedFetch(path, { method: 'POST', body: form });
  } catch (error) {
    if (error instanceof RequestError) throw error;
    throw new RequestError(0, 'errors.network');
  }

  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? ((await response.json()) as ApiErrorPayload | null) : null;
    throw new RequestError(response.status, payload?.error?.code ?? 'errors.unknown', payload?.error?.fields ?? {});
  }

  return { blob: await response.blob(), headers: response.headers };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, opts?: { anonymous?: boolean; signal?: AbortSignal }) =>
    request<T>(path, { method: 'POST', body, ...opts }),
  put: <T>(path: string, body?: unknown, opts?: { signal?: AbortSignal }) => request<T>(path, { method: 'PUT', body, ...opts }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData, signal?: AbortSignal) => request<T>(path, { method: 'POST', form, signal }),
  uploadBlob,
  download,
  openFile,
  keepalive: <T>(path: string, method: 'POST' | 'PUT', body: unknown) =>
    request<T>(path, { method, body, keepalive: true }),
};

/**
 * AUTH-009 — the subscription screen's three routes.
 *
 * Typed here rather than at the render site so the page never restates a path
 * or a response shape, and so the admin-only routes are visible as a group.
 * `checkout` answers with the provider's hosted page; nothing about the plan
 * has changed yet when it resolves.
 */
export const billingApi = {
  summary: () => api.get<BillingSummary>('/billing'),
  checkout: (plan: SubscriptionPlan) => api.post<{ url: string }>('/billing/checkout', { plan }),
  cancel: () => api.post<Subscription>('/billing/cancel'),
};

export { apiUrl } from './config.js';
